import React, { useState, useEffect } from 'react';

const StatusPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch('/api/status');
        if (res.ok) setData(await res.json());
      } catch {} finally { setLoading(false); }
    };
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, []);

  const statusColor = (s) => s === 'operational' ? '#06f4ad' : s === 'degraded' ? '#f5a623' : '#f0485f';
  const statusLabel = (s) => s === 'operational' ? 'Operational' : s === 'degraded' ? 'Degraded' : 'Incident';
  const statusBg = (s) => s === 'operational' ? 'rgba(6,244,173,0.08)' : s === 'degraded' ? 'rgba(245,166,35,0.08)' : 'rgba(240,72,95,0.08)';
  const statusBorder = (s) => s === 'operational' ? 'rgba(6,244,173,0.15)' : s === 'degraded' ? 'rgba(245,166,35,0.15)' : 'rgba(240,72,95,0.15)';

  const fmtTime = (s) => {
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
    return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  };

  if (loading) {
    return (
      <section className="os-section" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="os-loading"><div className="loading-spinner large"></div><p>Loading status...</p></div>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="os-section" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--text-muted)' }}>Unable to load status data</p>
      </section>
    );
  }

  return (
    <>
      <section className="os-section" style={{ paddingTop: '3rem' }}>
        <div className="os-container" style={{ maxWidth: 720 }}>
          {/* Overall Status Banner */}
          <div style={{
            background: statusBg(data.overallStatus),
            border: `1px solid ${statusBorder(data.overallStatus)}`,
            borderRadius: 14, padding: '1.5rem 2rem', marginBottom: '2rem', textAlign: 'center'
          }}>
            <div style={{
              width: 12, height: 12, borderRadius: '50%',
              background: statusColor(data.overallStatus),
              display: 'inline-block', marginRight: 10,
              boxShadow: `0 0 8px ${statusColor(data.overallStatus)}`
            }}></div>
            <span style={{ fontSize: '1.1rem', fontWeight: 600, color: statusColor(data.overallStatus) }}>
              {data.overallStatus === 'operational' ? 'All Systems Operational' : data.overallStatus === 'degraded' ? 'Degraded Performance' : 'Active Incident'}
            </span>
          </div>

          {/* Key Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '2rem' }}>
            {[
              { label: 'Success Rate', value: `${data.stats.successRate}%`, color: data.stats.successRate > 95 ? 'var(--accent)' : 'var(--orb-orange)' },
              { label: 'Avg Delivery', value: data.stats.avgDeliveryTime > 0 ? fmtTime(data.stats.avgDeliveryTime) : '< 1m', color: 'var(--text-primary)' },
              { label: 'Orders Today', value: data.stats.ordersToday, color: 'var(--text-primary)' },
              { label: 'Uptime', value: `${data.stats.daysSinceIncident}d`, color: 'var(--accent)' }
            ].map((s, i) => (
              <div key={i} style={{
                background: 'var(--bg-card)', border: '1px solid var(--bg-card-border)',
                borderRadius: 10, padding: '1rem 1.25rem'
              }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.3rem' }}>{s.label}</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 600, color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Services */}
          <h2 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.75rem' }}>Services</h2>
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--bg-card-border)',
            borderRadius: 12, overflow: 'hidden', marginBottom: '2rem'
          }}>
            {data.services.map((s, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '0.85rem 1.25rem',
                borderBottom: i < data.services.length - 1 ? '1px solid var(--bg-card-border)' : 'none'
              }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{s.name}</span>
                <span style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: '0.78rem', fontWeight: 500, color: statusColor(s.status)
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: statusColor(s.status),
                    boxShadow: `0 0 6px ${statusColor(s.status)}`
                  }}></span>
                  {statusLabel(s.status)}
                </span>
              </div>
            ))}
          </div>

          {/* 7-Day Chart */}
          <h2 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.75rem' }}>Past 7 Days</h2>
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--bg-card-border)',
            borderRadius: 12, padding: '1.25rem', marginBottom: '2rem'
          }}>
            <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 80 }}>
              {data.dailyStats.map((d, i) => {
                const maxOrders = Math.max(...data.dailyStats.map(x => x.orders), 1);
                const h = Math.max(8, (d.orders / maxOrders) * 100);
                const color = d.rate >= 95 ? '#06f4ad' : d.rate >= 80 ? '#f5a623' : '#f0485f';
                return (
                  <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{
                      height: `${h}%`, background: color, borderRadius: '4px 4px 0 0',
                      opacity: 0.7, minHeight: 4, transition: 'height 0.3s'
                    }} title={`${d.date}: ${d.completed}/${d.orders} orders (${d.rate.toFixed(0)}%)`}></div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
              {data.dailyStats.map((d, i) => (
                <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                  {d.date.slice(5)}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginTop: '0.75rem', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#06f4ad', marginRight: 4 }}></span>≥95% success</span>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#f5a623', marginRight: 4 }}></span>80-95%</span>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#f0485f', marginRight: 4 }}></span>&lt;80%</span>
            </div>
          </div>

          {/* Active Incidents */}
          {data.activeIncidents.length > 0 && (
            <>
              <h2 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--red-primary)' }}>Active Incidents</h2>
              {data.activeIncidents.map((inc, i) => (
                <div key={i} style={{
                  background: 'rgba(240,72,95,0.05)', border: '1px solid rgba(240,72,95,0.12)',
                  borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '0.5rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: '0.85rem' }}>{inc.title}</strong>
                    <span style={{ fontSize: '0.7rem', color: 'var(--red-primary)', background: 'rgba(240,72,95,0.1)', padding: '2px 8px', borderRadius: 12 }}>{inc.severity}</span>
                  </div>
                  {inc.description && <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.35rem' }}>{inc.description}</p>}
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>Started {new Date(inc.createdAt).toLocaleString()}</div>
                </div>
              ))}
            </>
          )}

          {/* Recent Incidents */}
          {data.recentIncidents.length > 0 && (
            <>
              <h2 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.75rem', marginTop: '1rem' }}>Recent Incidents</h2>
              {data.recentIncidents.map((inc, i) => (
                <div key={i} style={{
                  background: 'var(--bg-card)', border: '1px solid var(--bg-card-border)',
                  borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '0.5rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: '0.85rem' }}>{inc.title}</strong>
                    <span style={{ fontSize: '0.7rem', color: 'var(--accent)', background: 'rgba(6,244,173,0.08)', padding: '2px 8px', borderRadius: 12 }}>resolved</span>
                  </div>
                  {inc.resolution && <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.35rem' }}>{inc.resolution}</p>}
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                    {new Date(inc.createdAt).toLocaleDateString()} — Resolved {new Date(inc.resolvedAt).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </>
          )}

          <div style={{ textAlign: 'center', marginTop: '2rem', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            Auto-refreshes every 30 seconds · Last updated {new Date().toLocaleTimeString()}
          </div>
        </div>
      </section>
    </>
  );
};

export default StatusPage;
